import { csvReader } from "../../utils/csvReader";
import { InternationalTradeData, InternationalTradeUnit } from "./models/IntTradeData";
import { InternationalTradeCategory } from "./models/IntTradeCategory";
import { pipe } from "../../utils/pipe";
import { groupBy, Grouped, sumBy, sortBy } from "../../utils/array";
import { InternationalTradeCountry } from "./models/IntTradeCountry";
import { InternationalTradeRegion } from "./models/IntTradeRegion";
import { InternationalTradeSubCategory } from "./models/IntTradeSubCategory";
import { round, percentageChange, percentage } from "../../utils/math";

export function getDataPoints() {
    return csvReader<InternationalTradeData>("/data-sources/international-trade/data.csv");
}

export function getCategories() {
    return csvReader<InternationalTradeCategory>("/data-sources/international-trade/categories.csv");
}

export function getCountries() {
    return csvReader<InternationalTradeCountry>("/data-sources/international-trade/countries.csv");
}

export function getRegions() {
    return csvReader<InternationalTradeRegion>("/data-sources/international-trade/regions.csv");
}

export function getSubCategories() {
    return csvReader<InternationalTradeSubCategory>("/data-sources/international-trade/subcategories.csv");
}

export function getAll() {
    return Promise.all<
        InternationalTradeData[],
        InternationalTradeCategory[],
        InternationalTradeSubCategory[],
        InternationalTradeRegion[],
        InternationalTradeCountry[]
    >([getDataPoints(), getCategories(), getSubCategories(), getRegions(), getCountries()]);
}

const countryPartOfRegion = (countryId: number, regionId: number, countries: InternationalTradeCountry[]) => {
    return countries.find(c => c.regionId === regionId && c.id === countryId) != null;
};


export function getFilteredData(
    filter: {
        category: InternationalTradeCategory | null;
        subcategory: InternationalTradeSubCategory | null;
        region: InternationalTradeRegion | null;
        country: InternationalTradeCountry | null;
    },
    data: { datapoints: InternationalTradeData[]; countries: InternationalTradeCountry[] }
) {
    if (data.datapoints.length === 0) {
        return [[] as InternationalTradeData[], null, null] as const;
    }

    // prettier-ignore
    let filteredData = sortBy(data.datapoints.filter(
        point =>
            // only return data with absolute money values (instead of percentage)
            point.unit === InternationalTradeUnit.money &&
            (
                // if only region set, check if country is part of region
                (filter.country === null && filter.region !== null && countryPartOfRegion(point.countryId, filter.region.id, data.countries)) ||
                // if country set, match country
                (filter.country !== null && point.countryId === filter.country.id)
            )
    ), point => point.year as number);

    const year = 2019;
    const swedishExportsCategoryId = 1;

    let marketExportValue = sumBy(
        filteredData.filter(point => point.categoryId === swedishExportsCategoryId && point.year === year),
        point => point.value as number
    );

    filteredData = sortBy(
        filteredData.filter(
            point =>
                // filter by active category
                point.categoryId === filter.category?.id &&
                // filter by subcategory or if no option selected, get datapoints with no subcategory (null)
                (point.subCategoryId === filter.subcategory?.id ||
                    (filter.subcategory == null && point.subCategoryId === null))
        ),
        field => field.year as number
    );

    if (filter.region !== null && filter.country === null) {
        filteredData = pipe(
            // group all points by the year
            (points: InternationalTradeData[]) => groupBy(points, item => item.year as number),

            /// map to single data point but sum values of each year
            (groups: Grouped<InternationalTradeData, number>[]) =>
                groups.map(
                    item =>
                        ({
                            year: item.key,
                            value: sumBy(item.items, point => point.value as number),
                            categoryId: item.items[0].categoryId,
                            countryId: item.items[0].countryId,
                            subCategoryId: item.items[0].subCategoryId,
                            unit: item.items[0].unit,
                            type: item.items[0].type,

                            // below fields are not used so no need to set them
                            id: -1
                        } as InternationalTradeData)
                ),

            // sort data points by year
            (points: InternationalTradeData[]) => sortBy(points, point => point.year as number)
        )(filteredData);
    }

    let annualChange: number | null = null;
    let exportShare: number | null = null;

    if (filteredData.length > 0) {
        // calculate annual change between the last two years
        const latestData = filteredData[filteredData.length - 1];
        const currentYear = latestData.year as number;
        const [currentYearSum, previousYearSum] = [currentYear, currentYear - 1].map(year =>
            sumBy(
                filteredData.filter(point => point.year === year),
                point => point.value as number
            )
        );

        annualChange = round(percentageChange(currentYearSum, previousYearSum), 2);
        exportShare = round(percentage(latestData.value as number, marketExportValue), 2);
    }

    return [filteredData, annualChange, exportShare] as const;
}
